import {
  useRef,
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Popover, Whisper } from "rsuite";
import { findWhere } from "underscore";
import { useAtomValue } from "jotai";
import classNames from "classnames";

import ReferenceInput from "./ReferencesComponents/ReferenceInput";
import useTrackingRef from "../../../hooks/useTrackingRef";
import { GetRecordName } from "../../../utils/RecordUtils";
import { callFunctionIfExists } from "utils/GeneralUtils";
import { recordViewAtom } from "atoms/GlobalAtoms";
import useModule from "hooks/useModule";
import Reference from "./Reference";

/**
 * @param {object} field - The field schema of this Reference.
 * @param {object} value - The default value of the Reference
 * @param {string} inputSize - How big the input should be
 * @param {function} onChange - Callback function for when Reference
 * @param {boolean} inline - Whether or not the reference should be inline
 * @param {boolean} disabeld - Whether or not the input is disabled.
 * @param {function} onOpen - Callback function for when the Reference is opened.
 * @param {function} onClose - Callback function for when the Reference is closed.
 */
const ReferenceCreate = (
  {
    field,
    value,
    inputSize = "sm",
    onChange,
    inline,
    disabled,
    onOpen,
    onClose,
    pageID,
    reusableViewReferenceFields,
  },
  ref
) => {
  const whisperRef = useRef();
  const hideReferencePopover = useAtomValue(recordViewAtom);
  const [selected, setSelected] = useState(null);
  const selectedRef = useTrackingRef(selected);
  const [keepOpen, setKeepOpen] = useState(false);
  const [hideReference, setHideReference] = useState(false);
  const { getModule } = useModule();
  const isFirstRender = useRef(true);

  const type = field?.FieldType;

  const isExternalModuleDS = useMemo(() => {
    if (!field?.SourcesList) return false;

    if (Array.isArray(field?.SourcesList)) {
      const splitSource = field.SourcesList[0].split(".")[0];

      return splitSource !== "Tenant" ? true : false;
    }
    if (typeof field?.SourcesList === "string") {
      const splitSource = field.SourcesList.split(".")[0];
      return splitSource !== "Tenant" ? true : false;
    }
    return false;
  }, [field?.SourcesList]);

  const displayText = useMemo(() => {
    if (!selected || !type) return "--";

    if (type === "SingleReference") {
      const mod = getModule(selected?._ModuleAPIName);
      return GetRecordName(mod, selected) || "--";
    }

    if (!Array.isArray(selected)) return "--";

    return (
      selected
        .map((val) => {
          const mod = getModule(val?._ModuleAPIName);
          return GetRecordName(mod, val) || "--";
        })
        .join(", ") || "--"
    );
  }, [selected, type, getModule]);

  // Sync value with local state
  useEffect(() => {
    if (JSON.stringify(value) === JSON.stringify(selectedRef.current)) return;

    value && setSelected(value);
  }, [value]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    callFunctionIfExists(onChange, selected);
    // eslint-disable-next-line
  }, [selected]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef.current?.open();
    },
  }));

  return (
    <Whisper
      preventOverflow
      ref={whisperRef}
      placement="autoVertical"
      trigger={keepOpen || disabled ? "none" : "click"}
      onOpen={onOpen ? onOpen : null}
      onClose={onClose ? onClose : null}
      speaker={
        <Popover
          className={classNames("p-0 m-0", {
            "d-none": hideReferencePopover || hideReference,
          })}
        >
          <Reference
            field={field}
            onClose={() => whisperRef.current.close()}
            selected={selected}
            setSelected={setSelected}
            setKeepOpen={setKeepOpen}
            setHideReference={setHideReference}
            defaultOpen
            oneTap={type === "SingleReference"}
            onChange={onChange}
            pageID={pageID}
            reusableViewReferenceFields={reusableViewReferenceFields}
            searchCustomField={
              (Array.isArray(field?.SourcesList) &&
                !!field?.SourcesList?.length &&
                field.SourcesList[0].split(".").pop() === "People") ||
              (typeof field?.SourcesList === "string" &&
                field.SourcesList.split(".").pop() === "People")
                ? "Name"
                : undefined
            }
            disableAddRecord={isExternalModuleDS}
            showDisplayButton={!isExternalModuleDS}
          />
        </Popover>
      }
    >
      <div className="w-100">
        <ReferenceInput
          text={displayText}
          handleOpen={null}
          disabled={disabled}
          inputSize={inputSize}
        />
      </div>
    </Whisper>
  );
};

export default forwardRef(ReferenceCreate);
